import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { FAQAccordion } from "@/components/site/FAQAccordion";
import { BlogMarkdownContent } from "@/components/site/BlogMarkdownContent";
import { SERVICES } from "@/lib/site";
import { WhatsAppLink } from "@/components/site/WhatsAppLink";
import { messageTopicForBlogPost } from "@/lib/whatsapp-message";
import { CATEGORY_SLUGS, esPostDeEmpleo, type BlogPost } from "@/lib/blog";
import { blogImageBase, blogImageSrcSet, heroAspectRatio } from "@/lib/blog-image";

export type PublishedBlogPost = BlogPost & { contentHtml: string };

interface BlogPostArticleProps {
  post: PublishedBlogPost;
  related: BlogPost[];
}

/**
 * Cuerpo completo de la página de un post: header (breadcrumb, categoría,
 * título, fecha), imagen de portada, contenido, CTAs, FAQ y relacionados.
 * Extraído de blog.$slug.tsx para que la página pública real y la vista
 * previa del admin (/blog-preview) compartan exactamente el mismo
 * layout sin duplicar JSX (specs/106-blog-vista-previa).
 */
export function BlogPostArticle({ post, related }: BlogPostArticleProps) {
  const linkedServices = SERVICES.filter((s) => post.serviceLinks.includes(s.slug));

  return (
    <article className="bg-background">
      {/* HEADER */}
      <header className="pt-28 pb-8 md:pt-36 md:pb-10">
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <nav aria-label="Breadcrumb" className="text-xs text-secondary/60">
            <ol className="flex flex-wrap items-center gap-1">
              <li>
                <Link to="/" className="hover:text-primary">
                  Inicio
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li>
                <Link to="/blog" className="hover:text-primary">
                  Blog
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li>
                <Link
                  // Rutas del router público (routeTree.gen.ts) — este componente se
                  // reutiliza también en la vista previa del admin (blog-preview.tsx),
                  // donde tsc solo ve el Register del router admin (ver tsconfig.admin.json).
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore — ruta/param válidos en el router público, no en el admin
                  to="/blog/categoria/$category"
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore — idem
                  params={{ category: CATEGORY_SLUGS[post.category] }}
                  className="hover:text-primary"
                >
                  {post.category}
                </Link>
              </li>
            </ol>
          </nav>

          <Link
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore — ruta/param válidos en el router público, no en el admin
            to="/blog/categoria/$category"
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore — idem
            params={{ category: CATEGORY_SLUGS[post.category] }}
            className="mt-6 inline-block rounded-md bg-primary/10 px-2.5 py-1 text-xs font-medium uppercase tracking-wide text-primary hover:bg-primary/20"
          >
            {post.category}
          </Link>

          <h1 className="mt-5 font-display text-4xl font-black leading-[1.1] tracking-tight text-secondary text-balance md:text-5xl">
            {post.h1}
          </h1>

          <p className="mt-5 text-lg text-secondary/75">{post.excerpt}</p>

          <div className="mt-6 flex items-center gap-4 text-xs text-secondary/55">
            <time dateTime={post.publishedAt}>
              {new Date(post.publishedAt).toLocaleDateString("es-PE", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </time>
            <span aria-hidden="true">·</span>
            <span>{post.readMinutes} min de lectura</span>
            {post.updatedAt && post.updatedAt.slice(0, 10) !== post.publishedAt.slice(0, 10) && (
              <>
                <span aria-hidden="true">·</span>
                <span>
                  Actualizado el{" "}
                  <time dateTime={post.updatedAt}>
                    {new Date(post.updatedAt).toLocaleDateString("es-PE", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })}
                  </time>
                </span>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO IMAGE */}
      <div className="mx-auto max-w-3xl px-5 pb-4 md:px-8">
        <figure>
          <BlogHeroImage
            src={blogImageBase(post)}
            alt={post.h1}
            category={post.category}
            imageWidth={post.imageWidth}
            imageHeight={post.imageHeight}
          />
          {post.imageCaption && (
            <figcaption className="mt-2 text-xs text-secondary/55">{post.imageCaption}</figcaption>
          )}
        </figure>
      </div>

      {/* BODY */}
      <div className="mx-auto max-w-3xl px-5 pb-16 md:px-8">
        <BlogMarkdownContent html={post.contentHtml} />

        <MidCTA label={post.ctaLabel} href={post.ctaHref} />

        {/* Servicios relacionados */}
        {linkedServices.length > 0 && (
          <aside className="mt-12 rounded-lg border border-border bg-surface p-6 md:p-7">
            <h3 className="font-display text-lg font-bold text-secondary">
              Servicios relacionados
            </h3>
            <p className="mt-1 text-sm text-secondary/70">
              Si te interesa profundizar, revisa cómo trabajamos estos servicios.
            </p>
            <ul className="mt-4 flex flex-wrap gap-2">
              {linkedServices.map((s) => (
                <li key={s.slug}>
                  <Link
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore — ruta pública, no en el router admin (ver arriba)
                    to={s.slug}
                    className="inline-flex rounded-lg border border-border bg-background px-3 py-1.5 text-sm font-medium text-secondary hover:border-primary hover:text-primary"
                  >
                    {s.name} →
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        )}
      </div>

      {/* FAQ */}
      {post.faqs && post.faqs.length > 0 && (
        <FAQAccordion items={post.faqs} title="Preguntas frecuentes" />
      )}

      {/* CTA FINAL — el de empleo lleva a /postular, nunca a cotizar */}
      {esPostDeEmpleo(post) ? (
        <section className="section-dark py-16 md:py-20">
          <div className="mx-auto max-w-3xl px-5 text-center md:px-8">
            <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-ink-warm md:text-4xl">
              ¿Quieres trabajar en campañas de marcas?
            </h2>
            <p className="mt-4 text-ink-warm/75">
              Postula gratis desde cualquier ciudad del Perú. Te contactamos por WhatsApp cuando
              haya una campaña que calce con tu perfil.
            </p>
            <div className="mt-7 flex justify-center">
              <Link
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore — ruta pública, no en el router admin (ver arriba)
                to="/postular"
                className="btn-cta inline-flex items-center justify-center rounded-lg px-7 py-3.5 text-base font-semibold"
              >
                Postular gratis →
              </Link>
            </div>
          </div>
        </section>
      ) : (
        <section className="section-dark py-16 md:py-20">
          <div className="mx-auto max-w-3xl px-5 text-center md:px-8">
            <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-ink-warm md:text-4xl">
              ¿Listo para llevar esto a tu marca?
            </h2>
            <p className="mt-4 text-ink-warm/75">
              Cuéntanos tu proyecto y te enviamos propuesta en menos de 72 horas. Sin compromiso.
            </p>
            <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore — ruta pública, no en el router admin (ver arriba)
                to="/cotizar"
                className="btn-cta inline-flex items-center justify-center rounded-lg px-7 py-3.5 text-base font-semibold"
              >
                Solicitar propuesta →
              </Link>
              <WhatsAppLink
                topic={messageTopicForBlogPost(post.h1)}
                intent="consultar"
                topicId="blog"
                className="inline-flex items-center justify-center rounded-lg border border-ink-warm/40 px-6 py-3.5 text-sm font-semibold text-ink-warm hover:border-ink-warm"
              >
                Hablar por WhatsApp
              </WhatsAppLink>
            </div>
          </div>
        </section>
      )}

      {/* RELACIONADOS */}
      {related.length > 0 && (
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-5 md:px-8">
            <h2 className="font-display text-2xl font-bold text-secondary md:text-3xl">
              Artículos relacionados
            </h2>
            <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <article
                  key={r.slug}
                  className="flex flex-col rounded-lg border border-border bg-background p-6 shadow-soft"
                >
                  <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                    {r.category}
                  </span>
                  <h3 className="mt-3 font-display text-lg font-bold leading-snug text-secondary">
                    <Link
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore — ruta/param públicos, no en el router admin (ver arriba)
                      to="/blog/$slug"
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore — idem
                      params={{ slug: r.slug }}
                      className="hover:text-primary"
                    >
                      {r.h1}
                    </Link>
                  </h3>
                  <p className="mt-2 text-sm text-secondary/70">{r.excerpt}</p>
                </article>
              ))}
            </div>
            <div className="mt-10 text-center">
              <Link to="/blog" className="text-sm font-semibold text-primary hover:underline">
                ← Ver todos los artículos
              </Link>
            </div>
          </div>
        </section>
      )}
    </article>
  );
}

function BlogHeroImage({
  src,
  alt,
  category,
  imageWidth,
  imageHeight,
}: {
  src: string;
  alt: string;
  category: string;
  imageWidth?: number;
  imageHeight?: number;
}) {
  // Cuando el post no tiene image_width/image_height persistidos (imagen
  // cargada por fuera del flujo de subida que sí las calcula — p. ej. URL
  // externa pegada directo en la fila de Supabase), medimos la imagen ya
  // cargada en el navegador para que el hero igual se adapte a su tamaño
  // real en vez de forzar el ratio 1200x630 y recortarla.
  const [measuredRatio, setMeasuredRatio] = useState<number | null>(null);
  const ratio =
    imageWidth && imageHeight
      ? heroAspectRatio(imageWidth, imageHeight)
      : (measuredRatio ?? heroAspectRatio());

  return (
    <div
      className="relative w-full overflow-hidden rounded-xl shadow-md"
      style={{ aspectRatio: ratio }}
    >
      {/* Fallback — siempre en DOM, visible cuando la imagen falla */}
      <div className="absolute inset-0 bg-surface-dark">
        <div className="absolute inset-0 bg-pattern-dots opacity-20 pointer-events-none" />
        <div
          className="absolute inset-y-0 right-[20%] w-0.5 bg-primary/50"
          style={{ transform: "skewX(-8deg)" }}
        />
        <div
          className="absolute inset-y-0 right-[16%] w-px bg-primary/25"
          style={{ transform: "skewX(-8deg)" }}
        />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
          <span
            className="font-display font-black leading-none tracking-tighter text-ink-warm/[0.04]"
            style={{ fontSize: "clamp(6rem, 20vw, 14rem)" }}
          >
            IY
          </span>
        </div>
        <div className="absolute bottom-5 left-6 pointer-events-none select-none">
          <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-primary">
            {category}
          </span>
          <div className="mt-1.5 h-px w-8 bg-primary/50" />
        </div>
      </div>
      {/* Imagen hero — cubre el fallback cuando carga */}
      <picture>
        <source
          srcSet={blogImageSrcSet(src)}
          sizes="(min-width: 768px) 736px, 100vw"
          type="image/webp"
        />
        <img
          src={src}
          alt={alt}
          width={imageWidth ?? 1200}
          height={imageHeight ?? 630}
          loading="eager"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
          onLoad={(e) => {
            if (imageWidth && imageHeight) return;
            const img = e.currentTarget;
            setMeasuredRatio(heroAspectRatio(img.naturalWidth, img.naturalHeight));
          }}
          onError={(e) => {
            e.currentTarget.parentElement?.style.setProperty("display", "none");
          }}
        />
      </picture>
    </div>
  );
}

function MidCTA({ label, href }: { label: string; href: string }) {
  return (
    <div className="mt-7 rounded-lg border border-primary/20 bg-primary/5 p-5 md:p-6">
      <p className="text-sm text-secondary/70">
        Propuesta a medida en menos de 72 horas. Sin compromiso.
      </p>
      <Link
        to={href}
        className="btn-cta mt-3 inline-flex rounded-lg px-5 py-2.5 text-sm font-semibold"
      >
        {label} →
      </Link>
    </div>
  );
}
